/* import __COLOCATED_TEMPLATE__ from './dialog-sheet.hbs'; */
/**

A sheet is a full screen modal with associated open/close animation and a header bar.
## Contextual Components

| Description | Contextual Component |
| --- | --- |
| Header | [dialog-header](./dialog-header.html) |
| Title | [dialog-header/title](./dialog-header/title.html) |
| Subheader | [dialog-header/subheader](./dialog-header/subheader.html) |
| Body | [dialog-body](./dialog-body.html) |
| Actions | [dialog-actions](./dialog-actions.html) |
| Dropdown | [sq-dropdown](./sq-dropdown.html) |
| Secondary Actions | [dialog-actions](./dialog-actions.html) |
| Actions / Secondary Actions -> Button | [dialog-actions/button](./dialog-actions/button.html) |
| Actions / Secondary Actions -> Button Close X | [dialog-actions/button-close-x](./dialog-actions/button-close-x.html) |
| Actions / Secondary Actions -> Icon Close X | [svg/icon-close-x](./svg/icon-close-x.html) |
| Actions / Secondary Actions -> Button Back Arrow | [dialog-actions/button-back-arrow](./dialog-actions/button-back-arrow.html) |

## How to show a sheet

1. Create a template based on one of the below examples.

2. Create a controller/component (example: `controllers/cash-drawers/show.js`) for the sheet to live in. Set all necessary variables, actions, etc for the sheet's content..

3. Register your sheet as a route in `dashboard/app/router.js` or an apps individual router file (example: `dashboard/app/router/account-settings-routes.js`).
```js
this.route('things', { path: '/cash-drawers'}, function() {
  this.route('show');
});
```

4. To open your sheet, use a `link-to` component (or `transitionTo` call) which transitions to its route (example: to open the cash-drawers show sheet, you would use `{{link-to "cash_drawers.show" shiftToken}}`).

5. Congrats! You should have a working sheet!

```example
@title Example
@description Sheets can render up to 2 buttons within it's action block. While on desktop the action block
is output to the header, the sheet is responsive and the actions are output to the footer on mobile.

@controller-properties
{
  showSheet: false
}

@hbs
{{#toggle-code-example exampleName="Sheet"
                       show=showSheet}}

  {{#dialog-sheet title="Basic Sheet"
                  close=(action (mut showSheet) false)
                  as |sheet|}}
    {{#sheet.actions as |actions|}}
      {{actions.button-primary label="Primary"}}
    {{/sheet.actions}}

    {{#sheet.body}}
      Hey! I am a sheet, made with a contextual dialog-sheet component!
    {{/sheet.body}}
  {{/dialog-sheet}}

{{/toggle-code-example}}
```

```example
@title Customizing the Title
@description <strong>This is highly discouraged and should be rarely needed. 98% of the time, you can just
pass a string to the <code>title</code> param.</strong> You can pass a <code>{{#sheet.title}}</code> block if you need to pass formatted HTML
to the title area for some reason. Make sure to remove the <code>title</code> param from the parent <code>dialog-sheet</code>
component as this param will override anything you pass to the block.

@controller-properties
{
  showSheet: false
}

@hbs
{{#toggle-code-example exampleName="Sheet"
                       show=showSheet}}

  {{#dialog-sheet close=(action (mut showSheet) false)
                  as |sheet|}}
    {{#sheet.title}}
      <span class="online-status-indicator online-status-indicator--online">Online</span> Taylor Swift
    {{/sheet.title}}

    {{#sheet.actions as |actions|}}
      {{actions.button-primary label="Primary"}}
    {{/sheet.actions}}

    {{#sheet.body}}
      Hey! I am a sheet, made with a contextual dialog-sheet component!
    {{/sheet.body}}
  {{/dialog-sheet}}

{{/toggle-code-example}}
```

```example
@title Custom Secondary Actions
@description You can customize the secondary actions (aka the ones on the left of the tile) using the `secondary-actions` contextual component.

@controller-properties
{
  showSheet: false,
  showBackArrow: false
}

@hbs
{{#toggle-code-example exampleName="Sheet"
                       show=showSheet}}

  {{#dialog-sheet title="Basic Sheet"
                  as |sheet|}}
    {{#sheet.secondary-actions as |actions|}}
      {{#if showBackArrow}}
        {{actions.button-back-arrow click=(action (mut showBackArrow) false)}}
      {{else}}
        {{actions.button-close-x click=(action (mut showSheet) false)}}
      {{/if}}
    {{/sheet.secondary-actions}}

    {{#sheet.actions as |actions|}}
      {{#if showBackArrow}}
        {{actions.button-primary label="Done"
                                 click=(action "close")}}
      {{else}}
        {{actions.button-primary label="Continue"
                                 click=(action (mut showBackArrow) true)}}
      {{/if}}
    {{/sheet.actions}}

    {{#sheet.body}}
      <p><strong>showBackArrow:</strong> {{showBackArrow}}</p>
      <p>
        Click above to change the secondary action!
      </p>
    {{/sheet.body}}
  {{/dialog-sheet}}

{{/toggle-code-example}}
```

```example
@title Dialog Sheet with Steps (aka Wizard aka Breadcrumbs)
@description You can pair `dialog-sheet` with `selector-control` to create a "wizard" layout with steps, using the yield `secondary-actions` and `subheader` blocks to hook things up as needed.

@controller-properties
{
  selectorControlItems: ['TRIANGLE_KEY', 'CIRCLE_KEY', 'SQUARE_KEY'],
  showSheet: false
}

@hbs
{{#toggle-code-example exampleName="Sheet"
                       show=showSheet}}

  {{#selector-control type="STEP"
                      items=selectorControlItems
                      as |selector|}}
    {{#dialog-sheet title="Sheet with Steps"
                    as |sheet|}}
      {{#sheet.secondary-actions as |actions|}}
        {{#if selector.isFirstItem}}
          {{actions.button-close-x click=(action (mut showSheet) false)}}
        {{else}}
          {{actions.button-back-arrow click=(action selector.previousAction)}}
        {{/if}}
      {{/sheet.secondary-actions}}

      {{#sheet.actions as |actions|}}
        {{#if selector.isLastItem}}
          {{actions.button-primary label="Save"}}
        {{else}}
          {{actions.button-primary label="Continue"
                                   click=(action selector.nextAction)}}
        {{/if}}
      {{/sheet.actions}}

      {{#sheet.subheader}}
        {{#selector.navigation as |nav|}}
          {{nav.item key="TRIANGLE_KEY" label="Triangle"}}
          {{nav.item key="CIRCLE_KEY" label="Circle"}}
          {{nav.item key="SQUARE_KEY" label="Square"}}
        {{/selector.navigation}}
      {{/sheet.subheader}}

      {{#sheet.body}}
        {{#selector.content as |content|}}
          {{#content.section key="TRIANGLE_KEY"}}
            <p>Hello I am some triangle content</p>
          {{/content.section}}
          {{#content.section key="CIRCLE_KEY"}}
            <p>Hi I'm a rad circle content</p>
          {{/content.section}}
          {{#content.section key="SQUARE_KEY"}}
            <p>Yo, I'm a square</p>
          {{/content.section}}
        {{/selector.content}}
      {{/sheet.body}}
    {{/dialog-sheet}}
  {{/selector-control}}

{{/toggle-code-example}}
```

### Parameters
@param {String} [title=null] - Optional. Localized string. Shown in the middle of the sheet header. Alternatively, you can use the yielded `title` contextual component block to customize the title however you please (as shown in the "Customizing the Title" code example above). In that case, make sure *not* to pass `title`, since it takes precedence over the yielded block.
@param {Action/Component} [close=null] - Optional. A `link-to` component (or action) used for the `x` in the header which closes the sheet. Alternatively, you can use the yielded `secondary-actions` contextual component block to customize the secondary/icon actions however you please (as shown in the "Custom Secondary Actions" code example above). In that case, make sure *not* to pass `close`, since it takes precedence over the yielded block.
@param {Object} [flashContext=null] - Optional. Custom [Flash Context object](/glass/docs/development/flash.html). If present, will be passed to the `flash-portal` in the template instead of the default `sheet` context from the `flash` service.
@param {Boolean} [isLoading=false] - Optional. Show a loading spinner if you need to load data for the sheet body before displaying it.
@param {Boolean} [sheetBodyIsFullWidth=false] - Optional. Allows the passed body content to extend to the full width of the sheet.
@param {Boolean} [animateSlide=true] - Optional. When false, turns off the sliding animation of the sheet
*/

import Component from '@ember/component';
import { matchMedia } from '@square/glass-ui/utils/window';
import { bind, cancel, next } from '@ember/runloop';
import { inject as service } from '@ember/service';

// This class is necessary for CSS animation of the sheet opening
const START_POSITION_CLASS = 'sheet-layout--start-position';
// The associated scss media query is $small-media-query
// and 599px comes from $medium-breakpoint - 1
const SHEET_MOBILE_BREAKPOINT = 'screen and (max-width: 599px)';

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  ariaRole: 'dialog',
  attributeBindings: ['data-test-sheet', 'isTransitioning:data-sheet-is-transitioning'],
  'data-test-sheet': true,
  classNames: ['dialog', 'dialog--sheet', 'sheet-layout'],
  classNameBindings: ['sheetBodyIsFullWidth:dialog--sheet__content--full-width'],
  animateSlide: true,
  flash: service(),
  scroll: service(),
  title: null,
  close: null,
  flashContext: null,
  // This param powers the data-sheet-is-transitioning attribute, which
  // can be used by test utilities to ensure sheet animation is completed
  // before contining with tests
  isTransitioning: false,
  // only used internally by the component
  isMobile: true,
  sheetBodyIsFullWidth: false,
  closeAction: null,
  init(...args) {
    this._super(...args);

    // Set up a matchMedia query to set the isMobile
    // property on page resize
    this.mobileQuery = matchMedia(SHEET_MOBILE_BREAKPOINT);
    this.updateIsMobile = e => {
      this.set('isMobile', e.matches);
    };
    this.mobileQuery.addListener(this.updateIsMobile);

    // Fire the function manually once so the isMobile
    // param will be set correctly on first page load
    this.updateIsMobile(this.mobileQuery);
    this.get('scroll').disableWindowScrolling();
  },
  setupSlideAnimation() {
    this._onTransitionEnd = bind(this, function () {
      if (this.isDestroyed) {
        return;
      }
      this.set('isTransitioning', false);
    });
    this.element.addEventListener('transitionend', this._onTransitionEnd);
    this.element.classList.add(START_POSITION_CLASS);

    // Remove START_POSITION_CLASS so sheet slides up
    this.animationTask = next(() => {
      this.set('isTransitioning', true);
      this.element.classList.remove(START_POSITION_CLASS);
    });
  },
  teardownSlideAnimation() {
    cancel(this.animationTask);
    this.element.removeEventListener('transitionend', this._onTransitionEnd);
  },
  didInsertElement(...args) {
    this._super(...args);
    if (this.animateSlide) {
      this.setupSlideAnimation();
    }
  },
  willDestroyElement(...args) {
    this.mobileQuery.removeListener(this.updateIsMobile);
    if (this.animateSlide) {
      this.teardownSlideAnimation();
    }
    this.get('scroll').enableWindowScrolling();
    this._super(...args);
  }
});